import { TimelineSegment } from "../interfaces/timeline-segments";

export type TickType = 'major' | 'minor';

export interface TimelineTick {
  time: Date;
  type: TickType;
  label?: string;
  x: number;
  alignment: TickAlignment;
}
type TickAlignment = 'left' | 'center' | 'right';

export class TimelineEngine {
  private start!: number;
  private end!: number;
  private duration!: number;
  private width!: number;
  /** IANA zone for ruler labels. Empty uses the browser zone. */
  private tickTimeZone?: string;

  pixelsPerMinute = 20;
  padding = 40;

  init(config: {
    startDate: Date;
    endDate: Date;
    pixelsPerMinute?: number;
    timeZone?: string;
  }) {
    this.start = config.startDate.getTime();
    this.end = config.endDate.getTime();
    this.duration = this.end - this.start;
    const zone = config.timeZone?.trim();
    this.tickTimeZone = zone || undefined;

    if (config.pixelsPerMinute) {
      this.pixelsPerMinute = config.pixelsPerMinute;
    }

    const totalMinutes = this.duration / 60000;
    this.width = totalMinutes * this.pixelsPerMinute;
  }

  getTimelineWidth() {
    return this.width;
  }

  getRenderWidth() {
    return this.width + this.padding * 2;
  }

  getPixel(time: Date): number {
  return (
    ((time.getTime() - this.start) / this.duration) * this.width
  ) + this.padding;
  }

  getPercent(time: Date): number {
    return ((time.getTime() - this.start) / this.duration) * 100;
  }

  getTimeFromPixel(pixel: number): Date {
  const timelinePixel = pixel - this.padding;

  const clampedPixel = Math.max(
    0,
    Math.min(timelinePixel, this.width)
  );

  const ratio = clampedPixel / this.width;
  const time = this.start + ratio * this.duration;

  return new Date(time);
  }

  generateTicks(): TimelineTick[] {
    const totalMinutes = this.duration / 60000;
    const ticks: TimelineTick[] = [];

    let minorEveryMinutes = 1;
    let majorEveryMinutes = 10;

    if (this.pixelsPerMinute < 5) {
      minorEveryMinutes = 10;
      majorEveryMinutes = 60;
    } else if (this.pixelsPerMinute < 10) {
      minorEveryMinutes = 5;
      majorEveryMinutes = 30;
    } else {
      minorEveryMinutes = 1;
      majorEveryMinutes = 10;
    }

    for (let minute = 0; minute <= totalMinutes; minute += minorEveryMinutes) {
      const time = new Date(this.start + minute * 60000);
      const isMajor = minute % majorEveryMinutes === 0;
      let  alignment:TickAlignment = 'center';
 
      if (minute === 0)
        alignment = 'left';

      if (minute >= totalMinutes - majorEveryMinutes)
        alignment = 'right';

      ticks.push({
        time,
        type: isMajor ? 'major' : 'minor',
        label: isMajor ? this.formatTickLabel(time) : undefined,
        x: this.getPixel(time),
        alignment: alignment
      });
    }

    return ticks;
  }

  zoomIn() {
    this.pixelsPerMinute = Math.min(this.pixelsPerMinute * 1.25, 80);
    this.recalculateWidth();
  }

  zoomOut() {
    this.pixelsPerMinute = Math.max(this.pixelsPerMinute / 1.25, 2);
    this.recalculateWidth();
  }

  private recalculateWidth() {
    const totalMinutes = this.duration / 60000;
    this.width = totalMinutes * this.pixelsPerMinute;
  }

  /** Hour and minute in the camera zone, so 01:01 Pacific is not the browser clock. */
  private formatTickLabel(time: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    };
    if (this.tickTimeZone) {
      options.timeZone = this.tickTimeZone;
    }
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(time);
    const read = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';
    const hour = read('hour') === '24' ? '00' : read('hour');
    return `${hour}:${read('minute')}`;
  }

  getSegmentStyle(seg: TimelineSegment) {
   
  const start = Math.max(
    seg.start.getTime(),
    this.start
  );

  const end = Math.min(
    seg.end.getTime(),
    this.end
  );

  const left =
    ((start - this.start) / this.duration) *
    this.width +
    this.padding;

  const width =
    ((end - start) / this.duration) *
    this.width;

  return {
    left: `${left}px`,
    width: `${width}px`
  };
}

clampDateToSegment(
    date: Date,
    segment: TimelineSegment
): Date {

    const clamped = Math.max(
        segment.start.getTime(),
        Math.min(
            date.getTime(),
            segment.end.getTime()
        )
    );

    return new Date(clamped);
}

clampPixelToSegment(
    pixel: number,
    segment: TimelineSegment
): number {

    const startPx = this.getPixel(segment.start);
    const endPx = this.getPixel(segment.end);

    return Math.max(
        startPx,
        Math.min(pixel, endPx)
    );
}

getSegmentPixels(seg: TimelineSegment) {

    const left = this.getPixel(seg.start);
    const right = this.getPixel(seg.end);

    return {
        left,
        right,
        width: right - left
    };
}
}